import type { IsoDate } from './dates';
import { toCents } from './money';
import { defaultPrice } from './prices';
import type { Product, Store, StoreId } from './types';

export interface Offer {
  storeId: StoreId;
  productId: string;
  /**
   * DE: Angebotspreis pro Standardpackung in Euro.
   * EN: Offer price per standard package in euros.
   */
  packagePrice: number;
  validFrom: IsoDate;
  validTo: IsoDate;
  source: 'manual' | 'import';
}

/**
 * DE: Regulärer Preis laut eigenem Kassenbon, gültig ab `date`.
 * EN: Regular price from an own receipt, valid from `date`.
 */
export interface OwnPrice {
  productId: string;
  storeId: StoreId;
  price: number;
  date: IsoDate;
}

export interface PriceBook {
  catalog: ReadonlyMap<string, Product>;
  stores: readonly Store[];
  offers: readonly Offer[];
  ownPrices: readonly OwnPrice[];
}

export interface PackagePrice {
  storeId: StoreId;
  priceCents: number;
  regularCents: number;
  onOffer: boolean;
  /**
   * DE: true, wenn der Preis nur geschätzt ist (weder Kassenbon noch günstigeres Angebot).
   * EN: true when the price is only an estimate (neither a receipt nor a cheaper offer).
   */
  estimated: boolean;
}

/**
 * DE: Katalog aus eingebauten und eigenen Produkten; doppelte IDs sind ein Fehler.
 * EN: Catalog of built-in and own products; duplicate ids are an error.
 */
export function createCatalog(products: readonly Product[]): ReadonlyMap<string, Product> {
  const catalog = new Map<string, Product>();
  for (const product of products) {
    if (catalog.has(product.id)) throw new Error(`Duplicate product id: ${product.id}`);
    catalog.set(product.id, product);
  }
  return catalog;
}

/**
 * DE: Produkt nachschlagen; unbekannte IDs sind ein Fehler.
 * EN: Look up a product; unknown ids are an error.
 */
export function productOf(catalog: ReadonlyMap<string, Product>, id: string): Product {
  const product = catalog.get(id);
  if (!product) throw new Error(`Unknown product: ${id}`);
  return product;
}

function storeOf(book: PriceBook, storeId: StoreId): Store {
  const store = book.stores.find((candidate) => candidate.id === storeId);
  if (!store) throw new Error(`Unknown store: ${storeId}`);
  return store;
}

function regularPrice(
  book: PriceBook,
  product: Product,
  store: Store,
  date: IsoDate,
): { cents: number; fromReceipt: boolean } {
  const receipts = book.ownPrices.filter(
    (own) => own.productId === product.id && own.storeId === store.id && own.date <= date,
  );
  const newest = receipts.reduce<OwnPrice | undefined>(
    (latest, own) => (!latest || own.date >= latest.date ? own : latest),
    undefined,
  );
  if (newest) return { cents: toCents(newest.price), fromReceipt: true };
  return { cents: toCents(defaultPrice(product, store)), fromReceipt: false };
}

function cheapestOfferCents(book: PriceBook, productId: string, storeId: StoreId, date: IsoDate) {
  const active = book.offers.filter(
    (offer) =>
      offer.productId === productId &&
      offer.storeId === storeId &&
      offer.validFrom <= date &&
      date <= offer.validTo,
  );
  return Math.min(...active.map((offer) => toCents(offer.packagePrice)));
}

/**
 * DE: Packungspreis an einem Tag: Kassenbon oder Richtpreis, außer ein Angebot ist günstiger.
 * EN: Package price on a date: receipt or estimate, unless an offer is cheaper.
 */
export function packagePrice(
  book: PriceBook,
  productId: string,
  storeId: StoreId,
  date: IsoDate,
): PackagePrice {
  const store = storeOf(book, storeId);
  const regular = regularPrice(book, productOf(book.catalog, productId), store, date);
  const offer = cheapestOfferCents(book, productId, storeId, date);
  const onOffer = offer < regular.cents;
  return {
    storeId,
    priceCents: onOffer ? offer : regular.cents,
    regularCents: regular.cents,
    onOffer,
    estimated: !onOffer && !regular.fromReceipt,
  };
}

/**
 * DE: Günstigster Packungspreis unter den gewählten Märkten; bei Gleichstand zählt die Reihenfolge.
 * EN: Cheapest package price among the selected stores; ties keep the list order.
 */
export function bestPrice(
  book: PriceBook,
  productId: string,
  storeIds: readonly StoreId[],
  date: IsoDate,
): PackagePrice {
  const prices = storeIds.map((storeId) => packagePrice(book, productId, storeId, date));
  const best = prices.reduce<PackagePrice | undefined>(
    (cheapest, price) => (!cheapest || price.priceCents < cheapest.priceCents ? price : cheapest),
    undefined,
  );
  if (!best) throw new Error('At least one store is required');
  return best;
}
